<template>
  <div>
    <b-row class="small-text component-treatmentHx">
      <b-col cols="12" lg="6">
        <b-card body-class="p-2">
          <b-row>
            <b-col cols="3">
              <b-avatar
                class="mb-1"
                :variant="patient.contentId ? 'white' : 'info'"
                :text="abbreviation()"
                alt="User Image"
                size="60px"
                :src="viewFile(patient.contentId)"
                square
                rounded
              ></b-avatar>
            </b-col>
            <b-col cols="9">
              <div
                class="float-right"
                :style="{ display: 'flex', alignItems: 'center' }"
              >
                <HealthLinkButton :patient="patient" />
                <b-button
                  variant="primary"
                  size="sm"
                  type="button"
                  class="ml-2 btn btn-info"
                  @click="
                    handleGenerateClinicCode(patient.id, patient.branchId)
                  "
                  v-if="!isConnected"
                  :disabled="isGenerating || isCheckingStatus"
                  v-b-tooltip.hover
                  title="เชื่อมต่อกับระบบ DentCare"
                >
                  <b-spinner
                    small
                    v-if="isGenerating || isCheckingStatus"
                    class="mr-1"
                  ></b-spinner>
                  <i v-else class="fas fa-qrcode mr-1"></i>
                  {{ isCheckingStatus ? "กำลังตรวจสอบ..." : "สร้างรหัส" }}
                </b-button>
                <span
                  v-if="isConnected"
                  v-b-tooltip.hover
                  :title="unlinkTooltipText"
                  class="d-inline-block"
                  tabindex="0"
                >
                  <b-button
                    size="sm"
                    type="button"
                    class="ml-2 btn"
                    @click="handleUnlinkClinic()"
                    :disabled="!(isManager || isHeadCounter)"
                  >
                    <i class="fas fa-unlink mr-1"></i> ตัดการเชื่อม
                  </b-button>
                </span>
                <b-button class="ml-2" size="sm" @click="editCustomer(patient)">
                  <i class="fas fa-info"></i>
                </b-button>
              </div>

              <p class="mb-0 narrow-spacing">
                DN: {{ patient.dn }}
                <CopyToClipboard :text="patient.dn" />
              </p>
              <p
                v-if="patient.existDn"
                class="mb-1 text-bold text-muted description narrow-spacing"
              >
                DN(เดิม): {{ patient.existDn }}
                <CopyToClipboard :text="patient.existDn" />
              </p>
              <p class="mb-1 narrow-spacing">
                <strong>{{ fullName() }}</strong>
                <i v-if="patient.dob">( {{ calAge(patient.dob) }} ปี )</i>
              </p>
              <p v-if="patient.congenitalDisease" class="text-danger mb-1">
                โรคประจำตัว:{{ patient.congenitalDisease }}
              </p>
              <p v-if="patient.allergic" class="text-danger mb-1">
                แพ้ยา: {{ patient.allergic }}
              </p>
              <p v-if="isPatientDoctor" class="mb-0">
                แพทย์ประจำ:
                {{ getDoctorListBy(patient.doctorId, "uid").fullName }}
              </p>
            </b-col>
          </b-row>
        </b-card>
        <div v-if="isItemTableBusy" class="text-center mb-3">
          <b-spinner variant="primary" type="grow"></b-spinner>
        </div>
        <div v-else>
          <b-row>
            <b-col cols="12" sm="4">
              <b-button
                class="mb-3"
                size="sm"
                variant="primary"
                :disabled="!hasOrthoPackage"
                @click="openPatientOrthoPkgModal"
                block
                >{{
                  hasOrthoPackage ? "แพ็กเกจจัดฟัน" : "ไม่มีแพ็กเกจจัดฟัน"
                }}</b-button
              ></b-col
            >
            <b-col cols="12" sm="4">
              <b-button class="mb-3" v-b-toggle.tx-hx-filter size="sm" block
                >ค้นหาประวัติโดยละเอียด</b-button
              >
            </b-col>
            <b-col cols="12" sm="4">
              <b-button 
                class="mb-3" 
                variant="success" 
                size="sm" 
                block
                @click="showPrintFilterModal"
                :disabled="filteredTreatmentItems.length === 0"
              >
                <i class="fas fa-print mr-2"></i>พิมพ์ประวัติการรักษา
              </b-button>
            </b-col>
          </b-row>
          <b-collapse id="tx-hx-filter">
            <b-row>
              <b-col cols="12" md="6" order="1" order-md="1">
                <b-form-group
                  class="mb-1"
                  label="ซี่ฟัน:"
                  label-cols-sm="3"
                  label-align-sm="right"
                  label-class="d-none d-sm-block"
                >
                  <div id="tooth-filter">
                    <multiselect
                      class="col p-0 vmHideDropdown"
                      v-model="filterTooth"
                      :options="getToothList"
                      :multiple="true"
                      placeholder="เลือกซี่ฟัน"
                      track-by="id"
                      :custom-label="customToothLabel"
                    >
                    </multiselect>
                  </div>
                </b-form-group>
              </b-col>
              <b-col cols="12" md="6" order="3" order-md="2">
                <date-picker
                  class="mb-1"
                  v-model="dateRange"
                  type="date"
                  range
                  format="DD MMM YY"
                  :shortcuts="dateRangeShortcuts"
                  :disabled-date="notAfterToday"
                  placeholder="เลือกวันที่ต้องการค้นหา"
                  input-class="form-control form-control-sm"
                ></date-picker>
              </b-col>
              <b-col cols="12" md="6" order="2" order-md="3">
                <b-form-group
                  class="mb-1"
                  label="ค้นหา:"
                  label-cols-sm="3"
                  label-align-sm="right"
                  label-class="d-none d-sm-block"
                >
                  <b-input-group>
                    <b-form-input
                      v-model="treatmentFilter"
                      type="search"
                      placeholder="แพทย์/รายการ"
                      size="sm"
                      trim
                    ></b-form-input>
                  </b-input-group>
                </b-form-group>
              </b-col>
              <b-col cols="12" md="6" order="4">
                <b-form-group class="mb-1">
                  <b-form-checkbox-group
                    id="checkbox-group-1"
                    v-model="typeSelected"
                    :options="typeOptions"
                    value-field="val"
                    text-field="text"
                  ></b-form-checkbox-group>
                </b-form-group>
              </b-col>
            </b-row>
          </b-collapse>

          <b-card no-body class="overflow-auto">
            <b-list-group class="treatment-list-group" flush>
              <b-list-group-item
                v-for="treatment in filteredTreatmentItems"
                :key="treatment.treatId"
                :class="['flex-column', 'align-items-start', 'p-2', 'light-gray-bg', getCrossBranchClass(treatment.branchId)]"
                data-testid="treatment-history-row"
                :data-treat-id="treatment.treatId"
                @click="handleListClick(treatment)"
              >
                <div class="d-flex w-100">
                  <p class="font-weight-bold mb-0">
                    {{ formatDate(treatment.creationDt) }}
                  </p>
                  <span
                    v-if="isOtherBranch(treatment.branchId)"
                    class="branch-badge ml-2"
                  >
                    {{ treatment.branchName || 'สาขาอื่น' }}
                  </span>
                  <p v-if="treatment.nhsoVisitNo > 0" class="ml-1 mr-auto">
                    <img style="width: auto; height: 18px" :src="NHSOLogo" />
                  </p>
                  <p class="m-0 ml-auto"></p>
                  <p
                    v-if="canUploadMedia"
                    v-b-tooltip.hover
                    title="เพิ่มรูป"
                    class="text-muted m-0 ml-2"
                    @click="showUploadModal(treatment.treatId)"
                  >
                    <i class="fas fa-plus"></i>
                  </p>
                  <p
                    v-if="isToday(treatment.creationDt)"
                    class="text-muted m-0 ml-2"
                    v-b-tooltip.hover
                    title="แก้ไขการรักษา"
                    @click="openEditingTreatmentModal(treatment.appointmentId)"
                  >
                    <i class="fas fa-edit"></i>
                  </p>
                  <p
                    v-if="isModified(treatment)"
                    class="text-muted m-0 ml-2"
                    v-b-tooltip.hover
                    title="ดูการแก้ไขย้อนหลัง"
                    @click="openEditingHistory(treatment.treatId)"
                  >
                    <i class="fa fa-history"></i>
                  </p>
                  <p
                    v-if="isDrawNote(treatment.appointmentId)"
                    v-b-tooltip.hover
                    title="มีการบันทึกด้วยการเขียน"
                    class="text-muted m-0 ml-2"
                  >
                    <i class="fas fa-pencil-alt"></i>
                  </p>
                </div>
                <b-list-group flush>
                  <b-list-group-item
                    v-for="item in treatment.items"
                    :key="item.id"
                    class="p-2 border-secondary"
                    data-testid="treatment-item-row"
                    :data-item-id="item.id"
                    :active="treatment.treatId === selectedItem.treatId"
                  >
                    <div class="py-1">
                      <div class="d-flex w-100 justify-content-between">
                        <p class="font-weight-bold mb-1">
                          {{
                            item.treatmentCode
                              ? `[${item.treatmentCode}]-`
                              : ""
                          }}{{ item.treatment }}
                        </p>
                        <p class="mb-0">{{ item.toothNumberSurfaces }}</p>
                      </div>

                      <p v-if="item.dxName" class="mb-1">
                        Dx: {{ item.dxName }}
                      </p>
                      <p
                        v-if="item.note"
                        class="mb-1"
                        style="line-height: 1rem; white-space: pre-line"
                      >
                        {{ item.note }}
                        <CopyToClipboard :text="item.note" />
                      </p>
                      <hr class="mt-0 mb-1 mx-2" />
                      <div class="d-flex w-100 justify-content-between">
                        <small class="mb-0">
                          {{ item.quantity ? `จำนวน:${item.quantity} /` : ""
                          }}{{ item.price ? ` ราคา ${item.price} บาท` : "" }}
                        </small>
                        <small class="mb-0">
                          {{ item.doctor ? `แพทย์: ${item.doctor}` : "" }}
                        </small>
                        <!-- <small>{{ item }}</small> -->
                      </div>
                    </div>
                  </b-list-group-item>
                </b-list-group>
                <!-- {{ treatment }} -->
                <div class="text-right">
                  <span
                    v-if="
                      getUserListIncludeDeletedBy(treatment.creationUid, 'uid')
                    "
                    class="font-weight-light small"
                  >
                    บันทึกโดย:
                    {{
                      getUserListIncludeDeletedBy(treatment.creationUid, "uid")
                        .fullName
                    }}
                    {{ formatDateTime(treatment.creationDt) }}
                  </span>
                  <span
                    v-if="isModified(treatment)"
                    class="font-weight-light text-danger small"
                  >
                    แก้ไขโดย:
                    {{
                      getUserListIncludeDeletedBy(treatment.modifyUid, "uid")
                        .fullName
                    }}
                    {{ formatDateTime(treatment.modifyDt) }}
                  </span>
                </div>
              </b-list-group-item>

              <!-- ไม่มีประวัติ -->
              <b-list-group-item
                v-if="filteredTreatmentItems.length === 0"
                class="flex-column align-items-start"
              >
                <div class="d-flex w-100 justify-content-between">
                  <p class="mb-0 text-muted">ไม่พบประวัติการรักษา</p>
                </div>
              </b-list-group-item>
            </b-list-group>
          </b-card>
        </div>
      </b-col>
      <b-col cols="12" lg="6">
        <Gallery label="OPD Card" category="OpdCard" />
        <Gallery
          label="รูปวันที่รักษา"
          :displayList="fileListFilterByTreatmentId"
        />
        <b-card body-class="p-2" border-variant="primary">
          <b-row>
            <b-col cols="12" class="d-flex justify-content-center">
              <div v-if="nextVisitLoading" class="align-self-center">
                <b-spinner small></b-spinner>
              </div>
              <div v-else class="w-100">
                <div v-if="aptDraft.id">
                  <b-table-simple class="m-0" small responsive>
                    <b-thead>
                      <b-tr>
                        <b-th class="border-top-0" colspan="12" variant="info"
                          >Next visit</b-th
                        >
                      </b-tr>
                    </b-thead>
                    <b-tbody>
                      <b-tr>
                        <b-th>นัดกับแพทย์:</b-th>
                        <b-td>{{ aptDraft.doctor || "" }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-th>เพื่อ:</b-th>
                        <b-td>{{ aptDraft.treatmentItems || "" }}</b-td>
                      </b-tr>
                      <b-tr>
                        <b-th>ระยะเวลา:</b-th>
                        <b-td>{{ aptDraft.duration || "" }} นาที</b-td>
                      </b-tr>
                      <b-tr>
                        <b-th>นัดในอีก:</b-th>
                        <b-td
                          >{{
                            aptDraft.inDays ? aptDraft.inDays : ""
                          }}
                          วัน</b-td
                        >
                      </b-tr>
                      <b-tr>
                        <b-th>โน๊ตเพิ่มเติม:</b-th>
                        <b-td>{{ aptDraft.note || "" }}</b-td>
                      </b-tr>
                    </b-tbody>
                  </b-table-simple>
                </div>
                <div v-else class="d-flex justify-content-center">
                  <p class="text-muted m-2">ไม่มีข้อมูล Next visit</p>
                </div>
              </div>
            </b-col>
          </b-row>
        </b-card>
        <b-row>
          <b-col>
            <DisplayDrawNote
              ref="displayDrawNote"
              :drawNote="drawNote"
              :selectedItem="selectedItem"
            />
          </b-col>
        </b-row>
      </b-col>
      <b-popover
        id="toothNumberPop"
        target="tooth-filter"
        triggers="focus"
        placement="rightbottom"
      >
        <div style="width: 200px">
          <TeethDiagram
            v-model="filterTooth"
            @close="hideTeethDiagramPopover"
          />
        </div>
        <div>
          <b-row class="mt-1">
            <b-col class="pr-1">
              <b-button
                block
                variant="outline-info"
                class="p-0"
                size="sm"
                @click="filterTooth = []"
                >เคลียร์</b-button
              >
            </b-col>
            <b-col class="pl-1">
              <b-button
                block
                variant="outline-primary"
                class="p-0"
                size="sm"
                @click="hideTeethDiagramPopover"
                >ตกลง</b-button
              >
            </b-col>
          </b-row>
        </div>
      </b-popover>
      <PatientOrthoPkgModal
        ref="patientOrthoPkgModal"
        :patientId="patient.id"
      />
      <EditingHistoryModal ref="editingHistoryModal" />
      <UploadFilesModal
        :patientId="patient.id"
        :patientDn="patient.dn"
        ref="uploadFiles"
        @uploadedFiles="announceUploadedFiles"
      />
    </b-row>
    <GenerateClinicCode
      ref="generateClinicCode"
      :clinicCode="clinicCode"
      :patientData="patient"
      :patientId="patient.id"
      @openCustomerModal="editCustomer"
      v-if="clinicCode"
    >
    </GenerateClinicCode>
    <UnlinkClinic ref="unlinkClinic" :patientId="patient.id"></UnlinkClinic>
    <Loading v-if="isGenerating"></Loading>
    <Dialog ref="Dialog"></Dialog>
    <PdfExport ref="pdfExport" />

    <!-- Print Filter Modal -->
    <b-modal
      ref="printFilterModal"
      id="print-filter-modal"
      title="เลือกประเภทรายการที่ต้องการพิมพ์"
      ok-title="พิมพ์"
      cancel-title="ยกเลิก"
      @ok="handlePrintConfirm"
      @cancel="printFilter = 'all'"
    >
      <b-form-group>
        <b-form-radio-group
          v-model="printFilter"
          :options="[
            { text: 'เลือกรายการทั้งหมด', value: 'all' },
            { text: 'เลือกเฉพาะรายการ สปสช.', value: 'nhso' },
            { text: 'รายการที่ไม่ใช่ สปสช.', value: 'non-nhso' }
          ]"
          name="printFilterOptions"
          stacked
        ></b-form-radio-group>
      </b-form-group>
      <div class="mt-2 text-muted small">
        <strong>จำนวนรายการที่จะพิมพ์:</strong> {{ filteredTreatmentItemsForPrint.length }} รายการ
      </div>
    </b-modal>

    <!-- Hidden Print Sections -->
    <div class="d-none">
      <!-- Print Header -->
      <div ref="printHeader">
        <b-row>
          <b-col>
            <h4 class="mb-1">{{ branchInfo?.clinic?.name }}</h4>
            <p v-if="branchInfo && branchInfo.address" class="mb-1 my-justify">
              {{ branchInfo.address }}
            </p>
            <p class="mb-1">
              <span v-if="branchInfo && branchInfo.tel">
                โทร: {{ branchInfo.tel }}
              </span>
              <span v-if="branchInfo && branchInfo.line" class="ml-3">
                Line ID: {{ branchInfo.line }}
              </span>
            </p>
            <p v-if="branchInfo && branchInfo.website" class="mb-1">
              {{ branchInfo.website }}
            </p>
          </b-col>
        </b-row>
        <hr />
      </div>
    </div>

    <div class="d-none">
      <!-- Print Body -->
      <div ref="printBody">
        <div class="text-center mb-3">
          <h4>ประวัติการรักษาผู้ป่วย</h4>
          <h5 class="text-muted">Patient Treatment History</h5>
        </div>

        <b-row class="mb-3">
          <b-col cols="6">
            <p class="mb-1"><strong>วันที่พิมพ์:</strong> {{ todayFormatted }}</p>
            <p class="mb-1"><strong>เวลา:</strong> {{ currentTime }}</p>
          </b-col>
          <b-col cols="6" class="text-right">
            <p class="mb-1"><strong>พิมพ์โดย:</strong> {{ getUserInfo?.fullName || getUserInfo?.firstNameTh + ' ' + getUserInfo?.lastNameTh || 'ไม่ระบุชื่อ' }}</p>
            <p v-if="getUserInfo?.email" class="mb-1"><strong>อีเมล:</strong> {{ getUserInfo.email }}</p>
          </b-col>
        </b-row>

        <hr />

        <!-- Patient Information -->
        <div class="avoid-break mb-4">
          <h5 class="mb-3">ข้อมูลผู้ป่วย</h5>
          <b-row>
            <b-col cols="6">
              <p class="mb-1"><strong>DN:</strong> {{ patient.dn }}</p>
              <p v-if="patient.personalId" class="mb-1"><strong>เลขบัตรประชาชน:</strong> {{ patient.personalId }}</p>
              <p class="mb-1"><strong>ชื่อ-นามสกุล:</strong> {{ fullName() }} 
                <span v-if="patient.dob">({{ calAge(patient.dob) }} ปี)</span>
              </p>
              <p v-if="patientAddress" class="mb-1"><strong>ที่อยู่:</strong> {{ patientAddress }}</p>
            </b-col>
            <b-col cols="6">
              <p v-if="patient.congenitalDisease" class="mb-1 text-danger">
                <strong>โรคประจำตัว:</strong> {{ patient.congenitalDisease }}
              </p>
              <p v-if="patient.allergic" class="mb-1 text-danger">
                <strong>แพ้ยา:</strong> {{ patient.allergic }}
              </p>
              <p v-if="isPatientDoctor" class="mb-1">
                <strong>แพทย์ประจำ:</strong> {{ getDoctorListBy(patient.doctorId, "uid").fullName }}
              </p>
            </b-col>
          </b-row>
        </div>

        <hr />

        <!-- Treatment History -->
        <div class="mb-4">
          <h5 class="mb-3">ประวัติการรักษา</h5>
          
          <div v-if="filteredTreatmentItemsForPrint.length === 0" class="text-center text-muted py-4">
            <p>ไม่พบประวัติการรักษา</p>
          </div>

          <div v-else>
            <div 
              v-for="treatment in filteredTreatmentItemsForPrint" 
              :key="treatment.treatId" 
              class="treatment-entry avoid-break mb-4"
            >
              <!-- Treatment Date Header -->
              <div class="treatment-date-header avoid-break mb-2 p-2" style="background-color: #f8f9fa; border-left: 4px solid #007bff;">
                <h6 class="mb-0">
                  <i class="fas fa-calendar-alt mr-2"></i>
                  {{ formatDate(treatment.creationDt) }}
                  <span v-if="treatment.nhsoVisitNo > 0" class="ml-2">
                    <img style="width: auto; height: 18px" :src="NHSOLogo" alt="NHSO" />
                    <small class="ml-1 text-success">NHSO Visit</small>
                  </span>
                  <span v-if="isDrawNote(treatment.appointmentId)" class="ml-2">
                    <i class="fas fa-pencil-alt text-info"></i>
                    <small class="ml-1 text-info">มีการบันทึกด้วยการเขียน</small>
                  </span>
                </h6>
              </div>

              <!-- Treatment Items -->
              <div class="treatment-items-container pl-3">
                <div 
                  v-for="item in treatment.items" 
                  :key="item.id" 
                  class="treatment-item avoid-break p-3 mb-2 border rounded"
                >
                  <b-row>
                    <b-col cols="8">
                      <p class="mb-1 font-weight-bold">
                        <span v-if="item.treatmentCode" class="text-muted">[{{ item.treatmentCode }}]</span>
                        {{ item.treatment }}
                      </p>
                      <p v-if="item.dxName" class="mb-1">
                        <strong>Dx:</strong> {{ item.dxName }}
                      </p>
                      <p v-if="item.note" class="mb-1" style="white-space: pre-line">
                        <strong>หมายเหตุ:</strong> {{ item.note }}
                      </p>
                      <div class="d-flex justify-content-between">
                        <small class="text-muted">
                          <span v-if="item.quantity">จำนวน: {{ item.quantity }}</span>
                          <span v-if="item.price" class="ml-3">ราคา: {{ item.price }} บาท</span>
                        </small>
                        <small class="text-muted">
                          <span v-if="item.doctor">แพทย์: {{ item.doctor }}</span>
                        </small>
                      </div>
                    </b-col>
                    <b-col cols="4" class="text-right">
                      <p class="mb-1">
                        <strong>ซี่ฟัน:</strong> 
                        <span class="badge badge-secondary">{{ item.toothNumberSurfaces || '-' }}</span>
                      </p>
                    </b-col>
                  </b-row>
                </div>

                <!-- Empty treatment with drawing note -->
                <div v-if="treatment.items.length === 0" class="treatment-item avoid-break p-3 mb-2 border rounded bg-light">
                  <div class="text-center text-muted">
                    <i class="fas fa-pencil-alt fa-2x mb-2"></i>
                    <p class="mb-0">มีการบันทึกด้วยการเขียน (Drawing Notes)</p>
                  </div>
                </div>
              </div>

              <!-- Treatment Footer -->
              <div class="treatment-footer text-right pl-3">
                <small class="text-muted">
                  <span v-if="getUserListIncludeDeletedBy(treatment.creationUid, 'uid')">
                    บันทึกโดย: {{ getUserListIncludeDeletedBy(treatment.creationUid, 'uid').fullName }}
                    {{ formatDateTime(treatment.creationDt) }}
                  </span>
                  <span v-if="isModified(treatment)" class="text-danger ml-3">
                    แก้ไขโดย: {{ getUserListIncludeDeletedBy(treatment.modifyUid, 'uid').fullName }}
                    {{ formatDateTime(treatment.modifyDt) }}
                  </span>
                </small>
              </div>

              <hr v-if="filteredTreatmentItems.indexOf(treatment) < filteredTreatmentItems.length - 1" />
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="d-none">
      <!-- Print Footer -->
      <div ref="printFooter">
        <hr class="mt-4" />
        <b-row class="mt-3">
          <b-col class="text-center">
            <small class="text-muted">
              เอกสารนี้สร้างโดยระบบ DentCloud
            </small>
          </b-col>
        </b-row>
        <b-row>
          <b-col class="text-left">
            <small class="text-muted">
              วันที่ {{ todayFormatted }} เวลา {{ currentTime }}
            </small>
          </b-col>
          <b-col class="text-right">
            <small class="text-muted">
              พิมพ์โดย: {{ getUserInfo?.fullName || getUserInfo?.firstNameTh + ' ' + getUserInfo?.lastNameTh || 'ไม่ระบุชื่อ' }}
              <span v-if="getUserInfo?.email"> | {{ getUserInfo.email }}</span>
            </small>
          </b-col>
        </b-row>
      </div>
    </div>

    <PdfExport ref="pdfExport" />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";

// import DateRangePicker from "vue2-daterange-picker";

import CrossBranchMixin from "@/mixins/CrossBranchMixin";
import DisplayDrawNote from "@/components/modal/ProfileCustomer/DisplayDrawNote";
import TeethDiagram from "@/components/popover/TeethDiagram";
import PatientOrthoPkgModal from "@/components/modal/ProfileCustomer/PatientOrthoPkg";
import EditingHistoryModal from "@/components/modal/EditingHistory";
import CopyToClipboard from "@/components/CopyToClipboard";
import Gallery from "@/components/card/Gallery";
import UploadFilesModal from "@/components/modal/UploadFiles";
import HealthLinkButton from "@/components/button/HealthLink";

import NHSOLogo from "../../../assets/img/NHSO_logo.png";

import { eventBus } from "@/main";

import moment from "moment";

import "@/theme/modal/ProfileCustomer/TreatmentHx.scss";
import GenerateClinicCode from "@/components/modal/ProfileCustomer/GenerateClinicCode";
import UnlinkClinic from "@/components/modal/ProfileCustomer/UnlinkClinic";
import Loading from "@/components/Loading.vue";
import Dialog from "@/components/modal/Dialog";
import PdfExport from "@/components/PdfExport";
import { FEATURE } from "@/constants/entitlements";

export default {
  name: "TreatmentHx",
  mixins: [CrossBranchMixin],
  components: {
    DisplayDrawNote,
    TeethDiagram,
    PatientOrthoPkgModal,
    EditingHistoryModal,
    CopyToClipboard,
    Gallery,
    UploadFilesModal,
    HealthLinkButton,
    GenerateClinicCode,
    UnlinkClinic,
    Loading,
    Dialog,
    PdfExport,
  },
  props: ["patient", "isItemTableBusy"],
  data() {
    return {
      //date table
      isSelcetedAll: true,
      dateTableFields: [
        {
          key: "creationDt",
          label: "วันที่รักษา",
          sortable: true,
        },
        { key: "time", label: "เวลา" },
      ],

      //items table
      itemsTableFields: [
        { key: "creationDt", label: "วันที่", sortable: true },
        { key: "doctor", label: "แพทย์" },
        {
          key: "toothNumberSurfaces",
          label: "ซี่",
        },
        { key: "treatment", label: "รายการ" },
        { key: "price", label: "฿", formatter: this.$utils.formatPrice },
        { key: "txTypeName", label: "ประเภท", sortable: true },
      ],
      selectedItem: {},
      apptIdSelected: null,
      treatmentFilter: "",
      treatmentId: null,

      //next visit
      aptDraft: {
        id: null,
        doctor: "",
        treatmentName: "",
        durration: null,
        inDays: null,
        note: "",
      },
      nextVisitLoading: false,

      filterTooth: null,

      // toothId: null,
      toothHover: null,

      dateRange: [],

      typeSelected: [],
      typeOptions: [
        { text: "การรักษา", value: 1 },
        { text: "แพ็กเกจจัดฟัน", value: 2 },
        { text: "ยา และอุปกรณ์", value: 3 },
      ],

      drawNote: null,
      NHSOLogo,

      isConnected: false,
      clinicCode: null,
      isGenerating: false,
      isCheckingStatus: false,
      printFilter: 'all', // 'all', 'nhso', 'non-nhso'
    };
  },
  watch: {
    "patient.id": {
      handler(newId) {
        this.checkConnectClinic(newId);
      },
      immediate: true,
    },
  },
  computed: {
    ...mapGetters({
      viewFile: "moduleContent/viewFile",
      getTreatmentHistoryList: "moduleTreatment/getTreatmentHistoryList",
      getUserListIncludeDeletedBy: "moduleUser/getUserListIncludeDeletedBy",
      getUserList: "moduleUser/getUserList",
      getTreatDiagBy: "moduleAppointment/getTreatDiagBy",
      getAppointmentFormat: "moduleAppointment/getAppointmentFormat",
      getTreatmentBy: "moduleAppointment/getTreatmentBy",
      getTreatBy: "moduleAppointment/getTreatBy",

      getToothList: "moduleMaster/getToothList",
      getToothBy: "moduleMaster/getToothBy",

      getDoctorListBy: "moduleUser/getDoctorListBy",
      getDoctorList: "moduleUser/getDoctorList",

      getPatientOrthoPackage: "moduleTreatment/getPatientOrthoPackage",
      dateRangeShortcuts: "moduleMaster/getDateRangeShortcuts",
      hasSubscribed: "moduleBranch/hasSubscribed",
      entitlementFlag: "moduleBranch/entitlementFlag",
      getFileList: "moduleMediaFile/getFileList",
      getUserInfo: "moduleUser/getUserInfo",
      isManager: "moduleUser/isManager",
      isHeadCounter: "moduleUser/isHeadCounter",
      branchInfo: "moduleUser/getBranchInfo",
    }),
    canUploadMedia() {
      return this.entitlementFlag(FEATURE.STORAGE);
    },
    unlinkTooltipText() {
      if (this.isManager || this.isHeadCounter) {
        return "ตัดการเชื่อมต่อกับระบบ DentCare";
      } else {
        return "เฉพาะผู้จัดการและหัวหน้าเวชระเบียนเท่านั้นที่สามารถตัดการเชื่อมต่อได้";
      }
    },
    treatmentItems() {
      let allItems = this.getTreatmentHistoryList.map(i =>
        i.items.map(j => {
          return {
            ...j,
            appointmentId: i.appointmentId,
            creationUid: i.creationUid,
            creationDt: i.creationDt,
            modifyUid: i.modifyUid,
            modifyDt: i.modifyDt,
            treatId: i.id,
            nhsoVisitNo: i.appointment?.nhsoVisitNo,
            branchId: i.branchId,
            branchName: i.branch?.name || '',
          };
        })
      );
      let itemsList = [].concat(...allItems).map(i => this.formatItem(i));
      let drawingItem = this.getTreatmentHistoryList
        .filter(i => i.items.length < 1)
        .map(i => {
          if (i.drawNote?.lines?.length > 0) {
            return this.createDrawingItem(i);
          } else {
            return this.createEmptyItem(i);
          }
        });
      return itemsList.concat(drawingItem);
    },
    isItemSelected() {
      return !(
        this.selectedItem && // 👈 null and undefined check
        Object.keys(this.selectedItem).length === 0 &&
        this.selectedItem.constructor === Object
      );
    },
    filteredTreatmentItems() {
      let list = this.treatmentItems;
      //filter วันที่
      if (this.dateRange[0] && this.dateRange[1]) {
        list = list.filter(item =>
          moment(item.creationDt)
            .locale("th")
            .isBetween(
              moment(this.dateRange[0]),
              moment(this.dateRange[1]),
              "day",
              "[]"
            )
        );
      }

      // filter ซี่ฟัน
      if (this.filterTooth?.length > 0) {
        let filterToothName = this.filterTooth.map(i => i.name);
        list = list.filter(
          i =>
            filterToothName.filter(j => {
              return i.toothNo?.includes(j);
            }).length > 0
        );
      }

      // filter ชื่อรายการ แพทย์
      if (this.treatmentFilter) {
        let filter = this.treatmentFilter.toLowerCase();
        list = list.filter(i => {
          return (
            i.doctor?.toLowerCase().includes(filter) ||
            i.treatment?.toLowerCase().includes(filter)
          );
        });
      }
      //filter ประเภท
      if (this.typeSelected.length > 0) {
        list = list.filter(i => this.typeSelected.includes(i.txTypeId));
      }
      //group ตามวันที่
      list = this.groupListById(list);
      //เรียงวันที่
      list.sort((a, b) => {
        return new Date(b.creationDt) - new Date(a.creationDt);
      });
      return list;
    },
    isPatientDoctor() {
      return (
        this.patient?.doctorId &&
        this.getDoctorListBy(this.patient.doctorId, "uid")
      );
    },
    hasOrthoPackage() {
      return this.getPatientOrthoPackage?.packageId;
    },
    fileListFilterByTreatmentId() {
      if (!this.treatmentId) return [];
      return this.getFileList.filter(i => i.treatmentId === this.treatmentId);
    },
    todayFormatted() {
      return moment().locale("th").format("D MMMM YYYY");
    },
    currentTime() {
      return moment().format("HH:mm");
    },
    patientAddress() {
      if (!this.patient) return "";
      const { address, subDistrict, district, province, country, postcode } = this.patient;
      const addressArr = [address, subDistrict, district, province, country, postcode];
      return addressArr.filter(Boolean).join(" ");
    },
    filteredTreatmentItemsForPrint() {
      let items = this.filteredTreatmentItems;
      
      if (this.printFilter === 'nhso') {
        items = items.filter(treatment => {
          // Check if any item in this treatment has NHSO visit
          return treatment.nhsoVisitNo && treatment.nhsoVisitNo > 0;
        });
      } else if (this.printFilter === 'non-nhso') {
        items = items.filter(treatment => {
          // Check if this treatment has NO NHSO visit
          return !treatment.nhsoVisitNo || treatment.nhsoVisitNo === 0;
        });
      }
      
      return items;
    },
  },

  created() {},
  methods: {
    ...mapActions({
      fetchQueueAppointmentDraftByApptId:
        "moduleAppointment/fetchQueueAppointmentDraftByApptId",
      fecthMediaFiles: "moduleMediaFile/fecthMediaFiles",
      grantToken: "moduleFhir/grantToken",
      createClinicCode: "moduleTreatment/createClinicCode",
      updateUnlinkClinic: "moduleTreatment/updateUnlinkClinic",
      fetchPatientConnectClinicStatus:
        "moduleTreatment/fetchPatientConnectClinicStatus",
    }),
    async handleListClick(item) {
      try {
        this.selectedItem = item;
        this.treatmentId = item.treatId;
        if (this.apptIdSelected === this.selectedItem.appointmentId) return;
        this.apptIdSelected = this.selectedItem.appointmentId;
        this.fetchNextVisitData(this.apptIdSelected);
        this.applyDrawNote();
      } catch (err) {
        console.error(err);
      }
    },

    async handleGenerateClinicCode(patientId, branchId) {
      this.isGenerating = true;
      await this.createClinicCode({
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
        dc_branch_id: branchId,
        dc_patient_id: patientId,
      })
        .then(res => {
          this.clinicCode = res.data;
          this.$nextTick(() => {
            this.$refs.generateClinicCode.show();
          });
        })
        .catch(error => {
          console.info("error", error.response.status);
          if (error.response.status === 400) {
            this.$refs.Dialog.showAlertConfirm(
              "คนไข้เชื่อมคลินิกสำเร็จแล้ว",
              `กรุณากด "ตกลง" เพื่ออัพเดทสถานะการเชื่อมคลินิก`,
              "info",
              "ตกลง",
              "",
              false,
              false
            ).then(result => {
              if (result.isConfirmed) {
                this.checkConnectClinic(patientId)
                  .then(res => {
                    this.isConnected = res.data.is_connected;
                    eventBus.$emit("updateConnectClinic", true);
                  })
                  .catch(error => {
                    this.showDialogToast(
                      "danger",
                      `เกิดข้อผิดพลาดในการตรวจสอบการเชื่อมต่อคลินิก`
                    );
                  });
              }
            });
          }
        })
        .finally(() => {
          this.isGenerating = false;
        });
    },

    handleUnlinkClinic() {
      if (this.$refs.unlinkClinic) {
        this.$refs.unlinkClinic.show();
      } else {
        console.error("UnlinkClinic component is not available.");
      }
    },

    async checkConnectClinic(patientId) {
      if (!patientId) return;
      if (patientId) {
        const res = await this.fetchPatientConnectClinicStatus({
          clinicUrl: this.$route.params.clinicUrl,
          branchUrl: this.$route.params.branchUrl,
          patientId,
        });
        this.isConnected = res.data.is_connected;
      }
    },

    applyDrawNote() {
      this.drawNote = this.getTreatmentHistoryList.find(
        i => i.appointmentId === this.apptIdSelected
      )?.drawNote;
      this.$refs.displayDrawNote.assignDrawNote(this.drawNote);
      // this.$refs.displayDrawNote.resetDrawScale();
    },
    fetchNextVisitData(appointmentId) {
      this.nextVisitLoading = true;

      this.fetchQueueAppointmentDraftByApptId({
        id: appointmentId,
        clinicUrl: this.$route.params.clinicUrl,
        branchUrl: this.$route.params.branchUrl,
      })
        .then(res => {
          this.aptDraft = this.formatAppointmentDraft(res?.data);
        })
        .catch(err => {
          console.error(err);
        })
        .finally(() => {
          this.nextVisitLoading = false;
        });
    },
    openEditingHistory(treatmentId) {
      this.$refs.editingHistoryModal.show(treatmentId);
    },
    showUploadModal(treatmentId) {
      this.$refs["uploadFiles"].show({ treatmentId });
    },
    formatDayDate(date) {
      if (date) {
        return moment(date).locale("th").format("dd D MMM YY");
      } else {
        return "";
      }
    },
    formatDate(date) {
      if (date) {
        return moment(date).locale("th").format("llll");
      } else {
        return "";
      }
    },
    formatTime(date) {
      return moment(date).format("HH:mm");
    },
    formatDateTime(date) {
      return moment(date).locale("th").format("L LT");
    },
    editCustomer(patient) {
      this.$emit(
        "openCustomerModal",
        Object.assign({}, patient),
        this.appointment
      );
    },
    fullName() {
      if (this.patient) {
        var tilte = this.patient.titleNameTh
          ? this.patient.titleNameTh
          : this.patient.titleNameEn
          ? this.patient.titleNameEn
          : "";
        var firstName = this.patient.firstNameTh
          ? this.patient.firstNameTh
          : this.patient.firstNameEn
          ? this.patient.firstNameEn
          : "";
        var lastName = this.patient.lastNameTh
          ? this.patient.lastNameTh
          : this.patient.lastNameEn
          ? this.patient.lastNameEn
          : "";
        var fullName = [tilte, firstName, lastName].join(" ");
        return fullName;
      } else {
        return "";
      }
    },
    abbreviation() {
      if (
        this.patient &&
        (this.patient.firstNameEn || this.patient.lastNameEn)
      ) {
        var firstName = "";
        var lastName = "";
        if (this.patient.firstNameEn) {
          firstName = this.patient.firstNameEn.charAt(0);
        }
        if (this.patient.lastNameEn) {
          lastName = this.patient.lastNameEn.charAt(0);
        }
        return `${firstName}${lastName}`;
      } else {
        return "";
      }
    },

    formatItem(item) {
      let surfaces = [
        { id: 5, label: "O", order: 1 },
        { id: 3, label: "M", order: 2 },
        { id: 4, label: "D", order: 3 },
        { id: 1, label: "B", order: 4 },
        { id: 2, label: "Li", order: 5 },
      ];
      let surfacesTx =
        item.surfaceItems
          ?.map(i => surfaces.find(j => j.id === i))
          .sort((a, b) => a.order - b.order)
          .map(i => i.label)
          .join("") || "";

      let treatmentTypeName = {
        1: "การรักษา",
        2: "แพ็กเกจจัดฟัน",
        3: "ยา และอุปกรณ์",
      };
      let event = {
        id: item.id,
        actionUid: item.actionUid,
        appointmentId: item.appointmentId,
        treatId: item.treatId,
        creationUid: item.creationUid,
        creationDt: item.creationDt,
        modifyUid: item.modifyUid,
        modifyDt: item.modifyDt,
        doctor:
          this.getUserListIncludeDeletedBy(item.actionUid, "uid")?.fullName ||
          "",
        toothNo: item.toothItems,
        // toothNumberSurfaces: `${item.toothItems} ${surfacesTx}`,
        toothNumberSurfaces: (item.toothItems || "") + (surfacesTx || ""),
        treatment: item.name,
        treatmentCode:
          this.getTreatBy(Number(item.treatItems), "id")?.code || "",
        price: this.$utils.formatNumber(item.price),
        note: item.note,
        txTypeId: item.typeId,
        txTypeName: treatmentTypeName[item.typeId],
        dxName: this.getTreatDiagBy(item.diagItems, "id").nameEn,
        quantity: item.quantity,
        nhsoVisitNo: item.nhsoVisitNo,
        branchId: item.branchId,
        branchName: item.branchName,
      };
      return event;
    },

    createDrawingItem(treatment) {
      let event = {
        treatId: treatment.id,
        appointmentId: treatment.appointmentId,
        actionUid: treatment.creationUid,
        creationUid: treatment.creationUid,
        modifyUid: treatment.modifyUid,
        creationDt: treatment.creationDt,
        modifyDt: treatment.modifyDt,
        txTypeId: 1,
        treatment: "-- มีเขียนบันทึก --",
        branchId: treatment.branchId,
        branchName: treatment.branch?.name || '',
      };
      return event;
    },
    createEmptyItem(treatment) {
      let event = {
        treatId: treatment.id,
        appointmentId: treatment.appointmentId,
        actionUid: treatment.creationUid,
        creationUid: treatment.creationUid,
        modifyUid: treatment.modifyUid,
        creationDt: treatment.creationDt,
        modifyDt: treatment.modifyDt,
        txTypeId: 1,
        treatment: "-- ไม่มีบันทึกรายการรักษา --",
        branchId: treatment.branchId,
        branchName: treatment.branch?.name || '',
      };
      return event;
    },
    groupListById(list) {
      let groupedList = Object.values(
        list.reduce((result, item) => {
          if (!result[item.treatId]) {
            result[item.treatId] = {
              treatId: item.treatId,
              appointmentId: item.appointmentId,
              nhsoVisitNo: item.nhsoVisitNo,
              creationUid: item.creationUid,
              modifyUid: item.modifyUid,
              // actionUid: item.actionUid || item.creationUid,
              creationDt: item.creationDt,
              modifyDt: item.modifyDt,
              branchId: item.branchId,
              branchName: item.branchName,
              items: [],
            };
          }
          result[item.treatId].items.push(item);
          return result;
        }, {})
      );
      return groupedList;
    },

    formatAppointmentDraft(aptDraftRaw) {
      if (!aptDraftRaw?.id) {
        return {
          id: null,
          doctor: "",
          treatmentName: "",
          durration: null,
          inDays: null,
          note: "",
        };
      }
      let appointmentFormat = this.getAppointmentFormat(aptDraftRaw);
      const startDate = moment(aptDraftRaw.creationDt).startOf("day");
      const endDate = moment(appointmentFormat.start).startOf("day");
      const totalDiffDate = endDate.diff(startDate, "days", true);
      return {
        id: aptDraftRaw.id,
        doctor: appointmentFormat.extendedProps.doctorName,
        treatmentItems: aptDraftRaw.aptTreatItems
          ?.map(i => this.getTreatmentBy(i, "id").nameTh)
          .join(", "),
        duration: appointmentFormat.extendedProps.timeDuration,
        inDays: totalDiffDate,
        note: appointmentFormat?.extendedProps?.note,
      };
    },
    notAfterToday(date) {
      return date > new Date(new Date().setHours(0, 0, 0, 0));
    },
    openPatientOrthoPkgModal() {
      this.$refs.patientOrthoPkgModal.show();
    },
    openEditingTreatmentModal(appointmentId) {
      eventBus.$emit("openPaymentHistoryModalInEditingMode", appointmentId);
    },
    async select1stIndex() {
      await this.$nextTick();
      if (this.filteredTreatmentItems.length < 1) return;
      this.handleListClick(this.filteredTreatmentItems[0]);
    },
    customToothLabel(libs) {
      if (libs.label) {
        return `${libs.label}`;
      } else {
        return `${libs.name}`;
      }
    },
    hideTeethDiagramPopover() {
      this.$root.$emit("bv::hide::popover", "toothNumberPop");
    },
    isDrawNote(id) {
      let arrId = this.getTreatmentHistoryList
        .filter(i => i.drawNote?.lines?.length > 0)
        .map(i => i.appointmentId);
      return arrId.includes(id);
    },
    isModified(treatment) {
      if (
        this.getUserListIncludeDeletedBy(treatment.modifyUid, "uid") &&
        this.getUserListIncludeDeletedBy(treatment.creationUid, "uid")
      ) {
        return (
          treatment.modifyUid !== treatment.creationUid ||
          treatment.creationDt !== treatment.modifyDt
        );
      }
    },
    setDefaultValueTreatmenHx() {
      this.selectedItem = {};
      this.treatmentId = null;
      this.apptIdSelected = null;
      this.treatmentFilter = "";

      //next visit
      this.aptDraft = {
        id: null,
        doctor: "",
        treatmentName: "",
        durration: null,
        inDays: null,
        note: "",
      };

      this.filterTooth = null;
      // this.toothId = null;
      this.toothHover = null;
      this.dateRange = [];
      this.typeSelected = [];
      this.drawNote = null;
    },
    calAge(dateString) {
      if (!dateString) return;
      const date = moment(dateString, "YYYY-MM-DD");
      return moment().diff(date, "years");
    },
    isToday(dateTime) {
      return moment(dateTime).isSame(moment(), "day");
    },
    // The gallery lives in another component; hand it the registered files.
    announceUploadedFiles(files) {
      eventBus.$emit("mediaAdded", files);
    },
    showPrintFilterModal() {
      this.printFilter = 'all'; // Reset to default
      this.$refs.printFilterModal.show();
    },
    handlePrintConfirm() {
      if (this.filteredTreatmentItemsForPrint.length === 0) {
        this.$refs.Dialog.showAlertInfo(
          "ไม่สามารถพิมพ์ได้", 
          "ไม่พบประวัติการรักษาที่จะพิมพ์สำหรับตัวกรองที่เลือก", 
          "warning"
        );
        return;
      }
      this.generateTreatmentHistoryPdf();
    },
    async generateTreatmentHistoryPdf() {
      try {
        await this.$nextTick();
        
        await this.$refs.pdfExport.generatePdf({
          header: this.$refs.printHeader,
          body: this.$refs.printBody,
          footer: this.$refs.printFooter,
        });
        
      } catch (error) {
        console.error("PDF generation failed:", error);
        this.$refs.Dialog.showAlertInfo(
          "เกิดข้อผิดพลาด", 
          "ไม่สามารถสร้าง PDF ได้", 
          "error"
        );
      }
    },
  },
  async mounted() {
    eventBus.$on("updateDrawNote", e => {
      this.drawNote = e;
    });
    eventBus.$on(
      "setDefaultValueProfileCustomer",
      this.setDefaultValueTreatmenHx
    );
    eventBus.$on("updateConnectClinic", e => {
      this.isConnected = e;
    });
  },
  beforeDestroy() {
    eventBus.$off("updateDrawNote", e => {
      this.drawNote = e;
    });
    eventBus.$off(
      "setDefaultValueProfileCustomer",
      this.setDefaultValueTreatmenHx
    );
    eventBus.$off("updateConnectClinic", e => {
      this.isConnected = e;
    });
  },
};
</script>

<style>
</style>
